import Button from "../../../../lib/html5-software-library/src/ranj/components/buttons/Button";

export default class DraggableButton extends Button {
    constructor(skin, snapToTarget = true) {
        super(skin);

        this._snapToTarget = snapToTarget;

        this._originX = this.skin.x;
        this._originY = this.skin.y;

        this._dragTargets = [];
        this._currentDragTarget = null;

        this._allowDrag = true;

        this._onMouseDownBound = this._onMouseDown.bind(this);
        this.skin.addEventListener("mousedown", this._onMouseDownBound);

        this._onDragBound = this._onDrag.bind(this);
        this._onMouseUpBound = this._onMouseUp.bind(this);
    }

    destroy = function () {
        this.skin.removeEventListener("mousedown", this._onMouseDownBound);
        this.skin.removeEventListener("pressmove", this._onDragBound);
        this.skin.removeEventListener("pressup", this._onMouseUpBound);

        this.removeAllEventListeners();
    }

    addDragTarget(dragTarget) {
        this._dragTargets.push(dragTarget);
    }

    setDragTargets(dragTargets) {
        this._dragTargets = dragTargets;
    }

    snapBackToOrigin() {
        this.skin.x = this._originX;
        this.skin.y = this._originY;
    }

    moveBackToOrigin(duration) {
        duration = duration || 350;
        this.setSelected(false);
        this.setFocus(false);

        return new Promise((resolve) => {
            createjs.Tween.get(this.skin)
                .to({
                    x: this._originX,
                    y: this._originY
                }, duration, createjs.Ease.backOut)
                .call(() => {
                    this.dispatchEvent(new CustomEvent(DraggableButton.MOVED_BACK_TO_ORIGIN, { detail: {oldTarget: this._currentDragTarget}}));
                    this._currentDragTarget = null;
                    resolve();
                });
        });
    }

    releasedOnDragTarget(dragTarget) {
        if (!this._snapToTarget) {
            return;
        }

        // Allow drag target to place anchor differently (for drag targets which don't center their graphics)
        const localDragTargetSlotPos = dragTarget.slotAnchor ? { x: dragTarget.slotAnchor.x, y: dragTarget.slotAnchor.y } : { x: 0, y: 0 };

        const positionDelta = dragTarget.localToLocal(localDragTargetSlotPos.x, localDragTargetSlotPos.y, this.skin);
        const targetPosition = {
            x: this.skin.x + positionDelta.x,
            y: this.skin.y + positionDelta.y,
        };

        return new Promise((resolve) => {
            createjs.Tween.get(this.skin)
                .to(targetPosition, 150, createjs.Ease.sineIn)
                .call(() => {
                    this.dispatchEvent(new CustomEvent(DraggableButton.RELEASED_ON_DRAG_TARGET, { detail: {target: dragTarget}}));
                    resolve();
            });
        });
    }

    _onMouseDown({ stageX, stageY }) {
        if (!this._enabled || !this._allowDrag) {
            return;
        }

        createjs.Tween.removeTweens(this.skin);

        this.setSelected(false);
        this.setFocus(false);

        this._offsetX = stageX - this.skin.x * this.skin.stage.scaleX;
        this._offsetY = stageY - this.skin.y * this.skin.stage.scaleY;

        this._currentDragTarget = null;
        this.dispatchEvent(DraggableButton.DRAG_START);

        this.skin.addEventListener("pressmove", this._onDragBound);
        this.skin.addEventListener("pressup", this._onMouseUpBound);

        // move itself to top of parent container so it's always dragged above other options
        this.skin.parent.addChild(this.skin);
    }

    _onDrag({ stageX, stageY }) {
        if (!this._enabled || !this._allowDrag) {
            return;
        }

        this.skin.x = (stageX - this._offsetX) / this.skin.stage.scaleX;
        this.skin.y = (stageY - this._offsetY) / this.skin.stage.scaleY;

        let newDragTarget;
        for (var i = 0; i < this._dragTargets.length; i++) {
            var dragTarget = this._dragTargets[i];
            var localMousePositionOnDragTarget = dragTarget.globalToLocal(stageX, stageY);
            if (dragTarget.hitTest(localMousePositionOnDragTarget.x, localMousePositionOnDragTarget.y)) {
                newDragTarget = dragTarget;
                if (newDragTarget !== this._currentDragTarget) {
                    this.dispatchEvent(DraggableButton.TARGET_ENTER);
                }
                break;
            }
        }

        if (this._currentDragTarget && !newDragTarget) {
            this.dispatchEvent(DraggableButton.TARGET_EXIT);
        }

        this._currentDragTarget = newDragTarget;
    }

    _onMouseUp({ stageX, stageY }) {
        this.skin.removeEventListener("pressmove", this._onDragBound);
        this.skin.removeEventListener("pressup", this._onMouseUpBound);

        if (!this._enabled || !this._allowDrag) {
            return;
        }

        if (!this._currentDragTarget) {
            this.moveBackToOrigin();
        } else {
            this.releasedOnDragTarget(this._currentDragTarget);
            this.setSelected(true);
            this.setFocus(true);
        }

        this.dispatchEvent(DraggableButton.DRAG_END);
    }

    isSlotted() {
        return !!this.currentDragTarget;
    }

    get currentDragTarget() {
        return this._currentDragTarget;
    }

    get x() {
        return this.skin.x;
    }

    set x(value) {
        this.skin.x = value;
    }

    set y(value) {
        this.skin.y = value;
    }

    get y() {
        return this.skin.y;
    }

    set originX(value) {
        this._originX = value;
    }

    set originY(value) {
        this._originY = value;
    }

    set allowDrag(value) {
        this._allowDrag = value;
    }
}

DraggableButton.DRAG_START = "drag_started";
DraggableButton.DRAG_END = "drag_end";

DraggableButton.TARGET_ENTER = "target_enter";
DraggableButton.TARGET_EXIT = "target_exit";

DraggableButton.MOVED_BACK_TO_ORIGIN = "moved_back_to_origin";
DraggableButton.RELEASED_ON_DRAG_TARGET = "release_on_drag_target";
